import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Eye, EyeSlash } from "@phosphor-icons/react";

/**
 * Sensitive field masked by default. Click eye icon to reveal — each reveal
 * fires a REVEAL_PHI audit event server-side. Auto re-masks after AUTO_MASK_MS.
 *
 * HIPAA §164.514(d) minimum necessary: we don't block legitimate views, we
 * make every view an explicit, logged action.
 */
const AUTO_MASK_MS = 10_000;

function maskValue(value, kind) {
  if (!value || typeof value !== "string") return "—";
  const v = value.trim();
  if (v.length <= 4) return "•".repeat(v.length);
  // Show first 1 + last 3, mask the rest
  const head = v.slice(0, 1);
  const tail = v.slice(-3);
  const dots = "•".repeat(Math.max(3, v.length - 4));
  return `${head}${dots}${tail}`;
}

export default function MaskedValue({
  value,
  kind = "phi",                  // npi | dea | license | phone | phi
  resourceType = "UNKNOWN",
  resourceId = null,
  field = "value",
  className = "",
  alwaysReveal = false,
}) {
  const [revealed, setRevealed] = useState(alwaysReveal);

  useEffect(() => {
    if (!revealed || alwaysReveal) return;
    const t = setTimeout(() => setRevealed(false), AUTO_MASK_MS);
    return () => clearTimeout(t);
  }, [revealed, alwaysReveal]);

  const handleReveal = async (e) => {
    e?.stopPropagation();
    if (revealed) {
      setRevealed(false);
      return;
    }
    try {
      await api.post("/audit/reveal", {
        field,
        resource_type: resourceType,
        resource_id: resourceId,
      });
    } catch {
      // proceed with reveal anyway; the audit miss is non-blocking
    }
    setRevealed(true);
  };

  if (!value) return <span className={`text-gray-400 ${className}`}>—</span>;

  return (
    <span className={`inline-flex items-center gap-1 ${className}`} data-testid={`masked-${field}`}>
      <span className="font-mono">{revealed ? value : maskValue(value, kind)}</span>
      <button
        type="button"
        onClick={handleReveal}
        className="text-gray-400 hover:text-slate-900 transition-colors"
        title={revealed ? "Mask again" : "Reveal (logs an audit event)"}
        data-testid={`reveal-btn-${field}`}
      >
        {revealed ? <EyeSlash size={12} weight="bold" /> : <Eye size={12} weight="bold" />}
      </button>
    </span>
  );
}
